// POST /events — приём событий аналитики с клиента.
import { z } from 'zod'

/**
 * Одно событие. Имя берётся из реестра фронта (`shared/lib/analytics/events.ts`), поэтому
 * схема его не перечисляет: реестр меняется чаще, чем контракт, и держать список в двух местах
 * значит регулярно ронять отправку на 422 из-за события, которое просто добавили.
 *
 * ЧТО МОЖНО КЛАСТЬ В `params`. Числа, короткие строки-ключи и флаги. Тексты лучиков, имена
 * близких, даты рождения и города — НЕЛЬЗЯ ни при каких условиях: иначе аналитика становится
 * копией персональных данных, которую мы обещали не собирать (docs/legal.md). Ограничения
 * ниже — не про производительность, а про это: длинному тексту тут просто нет места.
 */
export const analyticsEventSchema = z.object({
  name: z.string().min(1).max(64),
  params: z
    .record(z.string(), z.union([z.string().max(64), z.number(), z.boolean(), z.null()]))
    .nullish(),
  /**
   * Время на устройстве. Часы у людей врут и сдвинуты на часовые пояса, поэтому серверное
   * время ставится рядом и считается по нему; клиентское нужно, только чтобы восстановить
   * порядок событий внутри одной сессии, когда пачка доехала с задержкой.
   */
  client_at: z.string().datetime(),
})
export type AnalyticsEventDto = z.infer<typeof analyticsEventSchema>

/**
 * Пачка событий.
 *
 * Отправляем пачками, а не по одному: мини-аппу сворачивают и открывают постоянно, и запрос
 * на каждое нажатие означал бы десятки запросов за сессию на мобильной сети. Сотня в пачке —
 * потолок с запасом: при обычной работе за раз уходит пять-двадцать.
 */
export const createAnalyticsEventsRequestSchema = z.object({
  events: z.array(analyticsEventSchema).min(1).max(100),
})
export type CreateAnalyticsEventsRequest = z.infer<typeof createAnalyticsEventsRequestSchema>

/** Сколько строк записано. Клиенту это не нужно, но по ответу видно, что пачка не потерялась. */
export const createAnalyticsEventsResponseSchema = z.object({
  accepted: z.number().int().min(0),
})
export type CreateAnalyticsEventsResponse = z.infer<typeof createAnalyticsEventsResponseSchema>
